import { useCallback, useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Globe, MediaImage, MoreHoriz, Plus, Sparks, Microphone } from 'iconoir-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Skeleton } from 'components/ui/skeleton';
import { Textarea } from 'components/ui/textarea';
import { VisibilityField, VisibilityBadge } from 'components/blocks/visibility-field';
import { useTheme } from 'components/core/theme-provider';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';
import {
  useGetAgentById,
  useUpdateAgentPersona,
  useUpdateWidget,
} from 'features/chat/hooks/use-agents';
import type {
  Agent,
  AgentFormality,
  AgentResponseStyle,
  AgentTechnicalLevel,
  IUpdateAgentPersonaPayload,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import {
  canEditAgentConfig,
  isAgentOwnerAccess,
  shouldShowSharedBadge,
} from 'pages/agents/utils/agent-share-access';
import { SharedBadge } from 'components/blocks/shared-badge';
import { OwnerBadge } from 'components/blocks/owner-badge';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import { AgentsConfigPreviewSection } from './preview-section/agents-config-preview-section';
import { AgentImageUploader } from './agent-image-uploader';

type EditAgentForm = {
  name: string;
  description: string;
  agent_type: boolean;
  visibility: ResourceVisibility;
};

const defaultValues: EditAgentForm = {
  name: '',
  description: '',
  agent_type: false,
  visibility: DEFAULT_RESOURCE_VISIBILITY,
};

const agentFormInputClassName =
  '!h-12 w-full self-stretch rounded-lg border border-[#e7e7e7] bg-white !px-5 !py-4 text-base font-normal leading-[1.6] shadow-none placeholder:text-[#b0b0b0] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-card';

const agentFormLabelClassName =
  'text-[14px] font-medium leading-[1.6] text-[#4f4f4f] dark:text-true-white';

const agentFormFieldClassName = 'flex w-full flex-col gap-[4px] space-y-0';

const figmaAgentTypeRadioClassName =
  'size-[18px] shrink-0 rounded-full border border-[#6d6d6d] bg-white text-transparent shadow-none ring-offset-0 focus:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-[5px] data-[state=checked]:border-black dark:border-[#6d6d6d] dark:bg-card dark:data-[state=checked]:border-black [&_svg]:hidden';

const buildUpdatePayload = (
  agent: Agent,
  values: EditAgentForm,
  projectKey: string,
  options?: { includeVisibility?: boolean }
): IUpdateAgentPersonaPayload => {
  const payload: IUpdateAgentPersonaPayload = {
    agent_id: agent.id,
    agent_type: agent.agent_type || '',
    name: values.name.trim(),
    language: agent.language || '',
    tags: agent.tags ?? [],
    domain_expertise: agent.domain_expertise ?? [],
    role: agent.role ?? '',
    description: values.description.trim(),
    logo_url: agent.logo_url || '',
    logo_id: agent.logo_id || '',
    response_style: agent.response_style as AgentResponseStyle,
    formality_level: agent.formality_level as AgentFormality,
    technical_level: agent.technical_level as AgentTechnicalLevel,
    project_key: projectKey,
    is_any_agent: values.agent_type,
  };

  if (options?.includeVisibility ?? true) {
    payload.visibility = values.visibility;
  }

  return payload;
};

type EditAgentProps = {
  agentId: string;
  projectKey: string;
  agent?: Agent;
  isLoading?: boolean;
  isSaving?: boolean;
  lastSaveTime?: Date | null;
  basePrompt: string;
  onBasePromptChange: (value: string) => void;
  onRegisterSave?: (saveFn: (() => Promise<void>) | null) => void;
};

export function EditAgent({
  agentId,
  projectKey,
  agent: agentProp,
  isLoading: isLoadingProp = false,
  isSaving = false,
  lastSaveTime = null,
  basePrompt,
  onBasePromptChange,
  onRegisterSave,
}: EditAgentProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const { data, isLoading: isFetchingAgent } = useGetAgentById(
    { id: agentId, projectKey },
    { enabled: !!agentId && !!projectKey }
  );
  const { mutateAsync: updateAgentPersona } = useUpdateAgentPersona();
  const { mutateAsync: updateWidget } = useUpdateWidget();

  const agent = agentProp ?? data?.agent;
  const isLoading = isLoadingProp || isFetchingAgent;
  const userId = useCommonStore((state) => state.user?.id);
  const isOwner = isAgentOwnerAccess(agent, userId);
  const canEdit = canEditAgentConfig(agent, userId);
  const showSharedBadge = shouldShowSharedBadge(agent, userId);
  // Avoid flashing Edit ↔ View before agent ownership and the current user are known
  // (same pattern as agent-details-top-bar).
  const isOwnershipPending = isLoading || !agent || userId == null;
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const isOwnerRef = useRef(isOwner);
  isOwnerRef.current = isOwner;

  const editAgentSchema = z.object({
    name: z.string().trim().min(1, t('AGENT_NAME_REQUIRED') || 'Agent name is required'),
    description: z.string().trim().min(1, t('DESCRIPTION_REQUIRED') || 'Description is required'),
    agent_type: z.boolean(),
    visibility: z.enum(['private', 'public']),
  });

  const form = useForm<EditAgentForm>({
    resolver: zodResolver(editAgentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const agentRef = useRef(agent);
  agentRef.current = agent;

  useEffect(() => {
    if (!agent) {
      return;
    }

    form.reset({
      name: agent.name || '',
      description: agent.description || '',
      agent_type: agent.is_any_agent || false,
      visibility: agent.visibility ?? DEFAULT_RESOURCE_VISIBILITY,
    });
  }, [agent, form]);

  const savePersona = useCallback(async () => {
    const currentAgent = agentRef.current;
    if (!currentAgent || !canEditRef.current) {
      return;
    }

    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const values = form.getValues();
    const includeVisibility = isOwnerRef.current;
    const hasPersonaChanges =
      values.name.trim() !== (currentAgent.name || '').trim() ||
      values.description.trim() !== (currentAgent.description || '').trim() ||
      values.agent_type !== (currentAgent.is_any_agent || false) ||
      (includeVisibility &&
        values.visibility !== (currentAgent.visibility ?? DEFAULT_RESOURCE_VISIBILITY));

    if (!hasPersonaChanges) {
      return;
    }

    await updateAgentPersona(
      buildUpdatePayload(currentAgent, values, projectKey, { includeVisibility })
    );
    // Widget / embed updates are owner-only (FE contract).
    if (isOwnerRef.current) {
      await updateWidget({
        agent_id: currentAgent.id,
        project_key: projectKey,
        regen_widget_id: false,
        widget: {
          ...currentAgent.widget,
          name: values.name.trim(),
          greeting:
            currentAgent.widget?.greeting ||
            (t('WELCOME_WITH_AGENT_NAME', { name: values.name.trim() }) ||
              `Welcome! My name is ${values.name.trim()}. If you need any assistance or have any questions, I'm here to help.`),
        },
      });
    }

    form.reset(values);
  }, [form, projectKey, t, updateAgentPersona, updateWidget]);

  useEffect(() => {
    onRegisterSave?.(canEdit ? savePersona : null);
    return () => onRegisterSave?.(null);
  }, [canEdit, onRegisterSave, savePersona]);

  const visibility = form.watch('visibility');

  return (
    <div
      data-testid="edit-agent-workspace"
      className="flex w-full flex-col items-stretch gap-4 lg:flex-row lg:gap-5"
    >
      <div className="min-w-0 flex-1">
        <div
          data-testid="edit-agent-card"
          className={cn(
            'flex w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm lg:max-h-[calc(100dvh-8rem)]',
            ui.dialog,
          )}
        >
          <div className={cn('shrink-0 px-6 pb-4 pt-6', ui.header)}>
            <div className="flex w-full flex-wrap items-center gap-2">
              {isOwnershipPending ? (
                <Skeleton className="h-8 w-36 shrink-0" aria-hidden />
              ) : (
                <h2 className={cn('shrink-0 text-[1.65rem] font-bold leading-tight tracking-tight', ui.title)}>
                  {canEdit
                    ? t('EDIT_AGENT') || 'Edit Agent'
                    : t('VIEW_AGENT') || 'View Agent'}
                </h2>
              )}
              {agent && !isOwnershipPending ? (
                <>
                  <VisibilityBadge value={visibility} />
                  {isOwner ? <OwnerBadge /> : null}
                  {showSharedBadge ? <SharedBadge /> : null}
                </>
              ) : null}
            </div>
            <p className={cn('mt-2 text-sm leading-relaxed', ui.subtitle)}>
              {isOwnershipPending
                ? t('CREATE_YOUR_OWN_UNIQUE_SETUP', {
                    defaultValue: 'Create your own unique setup',
                  })
                : !canEdit
                  ? t('YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT', {
                      defaultValue: 'You are not the owner of this agent.',
                    })
                  : !isOwner
                    ? t('SHARED_WITH_YOU_CAN_EDIT', {
                        defaultValue: 'Shared with you · You can edit',
                      })
                    : t('CREATE_YOUR_OWN_UNIQUE_SETUP', {
                        defaultValue: 'Create your own unique setup',
                      })}
            </p>
          </div>
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-5',
              '[scrollbar-width:thin] [scrollbar-color:#d1d1d1_transparent]',
              '[&::-webkit-scrollbar]:w-[7px] [&::-webkit-scrollbar-track]:bg-transparent',
              '[&::-webkit-scrollbar-thumb]:min-h-[48px] [&::-webkit-scrollbar-thumb]:rounded-[100px] [&::-webkit-scrollbar-thumb]:bg-[#d1d1d1]',
              'dark:[scrollbar-color:#d1d1d1_transparent] dark:[&::-webkit-scrollbar-thumb]:bg-[#d1d1d1]',
            )}
          >
          {isLoading || !agent ? (
            <div className="flex flex-col gap-6">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <Form {...form}>
              <form className="flex flex-col gap-6 pb-4">
                <fieldset
                  disabled={!canEdit}
                  className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0 disabled:opacity-70"
                >
                  <div className="flex flex-col gap-6 border-b border-[#e7e7e7] pb-6 dark:border-border">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className={agentFormFieldClassName}>
                          <FormLabel className={agentFormLabelClassName}>
                            {t('AGENT_NAME') || 'Agent Name'}
                            <span className="leading-[1.6] text-[#f04438]">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('AGENT_NAME_PLACEHOLDER') || 'e.g. Customer Support Agent'}
                              {...field}
                              disabled={!canEdit}
                              className={agentFormInputClassName}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem className={agentFormFieldClassName}>
                          <FormLabel className={agentFormLabelClassName}>
                            {t('DESCRIPTION') || 'Description'}
                            <span className="leading-[1.6] text-[#f04438]">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={
                                t('BRIEFLY_DESCRIBE_AI_AGENT') ||
                                'Briefly describe the AI agent you want to create'
                              }
                              {...field}
                              disabled={!canEdit}
                              className={agentFormInputClassName}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex w-full flex-col items-start gap-4 pt-2">
                      <AgentImageUploader
                        agent={agent}
                        disabled={!canEdit}
                        className="items-start justify-start"
                      />

                      <FormField
                        control={form.control}
                        name="visibility"
                        render={({ field }) => (
                          <FormItem className={agentFormFieldClassName}>
                            <FormControl>
                              <VisibilityField
                                value={field.value}
                                onValueChange={field.onChange}
                                idPrefix="edit-agent-visibility"
                                disabled={!isOwner}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* <div className="flex w-full flex-col items-start border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <FormField
                    control={form.control}
                    name="agent_type"
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col items-start gap-4 space-y-0">
                        <FormLabel className="shrink-0 text-sm font-semibold leading-[1.2] tracking-normal text-[#3d3d3d] dark:text-foreground">
                          {t('AGENT_TYPE') || 'Agent Type'}
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => field.onChange(value === 'true')}
                            value={field.value ? 'true' : 'false'}
                            className="flex shrink-0 items-start gap-8"
                          >
                            <div className="flex shrink-0 items-center gap-2">
                              <RadioGroupItem
                                value="false"
                                id="edit-single-shot"
                                className={figmaAgentTypeRadioClassName}
                              />
                              <FormLabel
                                htmlFor="edit-single-shot"
                                className="cursor-pointer whitespace-nowrap text-base font-normal leading-[1.6] text-[#3d3d3d] dark:text-foreground"
                              >
                                {t('SINGLE_SHOT') || 'Single Shot'}
                              </FormLabel>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <RadioGroupItem
                                value="true"
                                id="edit-multi-step"
                                className={figmaAgentTypeRadioClassName}
                              />
                              <FormLabel
                                htmlFor="edit-multi-step"
                                className="cursor-pointer whitespace-nowrap text-base font-normal leading-[1.6] text-[#3d3d3d] dark:text-foreground"
                              >
                                {t('MULTI_STEP') || 'Multi Step'}
                              </FormLabel>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div> */}

                  {/* <div className="flex flex-col gap-1 border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <FormLabel className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                    {t('INSTRUCTIONS') || 'Instructions'}
                  </FormLabel>
                  <div className="overflow-hidden rounded-xl border border-[#e7e7e7] bg-white dark:border-border dark:bg-card">
                    <Textarea
                      placeholder={
                        t('AGENT_INSTRUCTIONS_PLACEHOLDER') ||
                        'What does this agent do? How should it behave? What should it avoid doing?'
                      }
                      rows={6}
                      value={basePrompt}
                      onChange={(event) => onBasePromptChange(event.target.value)}
                      className="min-h-[188px] resize-none rounded-none border-0 px-4 pb-6 pt-4 text-base leading-[1.6] shadow-none placeholder:text-[#b0b0b0] focus-visible:ring-0"
                    />
                    <div className="flex items-center justify-between border-t border-[#f6f6f6] px-4 py-1.5 dark:border-border">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded border border-[#e7e7e7] bg-[#f6f6f6] px-2 py-1 text-sm font-medium text-[#6d6d6d] dark:border-border dark:bg-white/10 dark:text-muted-foreground"
                        >
                          <Sparks width={16} height={16} strokeWidth={1.75} />
                          {t('OPTIMIZE') || 'Optimize'}
                        </button>
                        <button
                          type="button"
                          className="inline-flex size-8 items-center justify-center rounded-full border border-[#fffff9] text-[#3d3d3d] dark:border-border dark:text-foreground"
                        >
                          <Microphone width={20} height={20} strokeWidth={1.75} />
                        </button>
                      </div>
                      <div className="flex h-6 items-center rounded-sm border border-[#e7e7e7] bg-[#f6f6f6] px-1.5 py-0.5 dark:border-border dark:bg-white/10">
                        <div className="size-3 rounded-sm border border-[#e7e7e7] dark:border-border" />
                      </div>
                    </div>
                  </div>
                </div> */}

                  {/* <div className="flex flex-col gap-4 border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                        {t('MODEL') || 'Model'}
                      </p>
                      <p className="mt-1 text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
                        {t('SELECT_MODEL_FOR_AGENT') || 'Select the model that should power your agent.'}
                      </p>
                    </div>
                    <Select defaultValue="auto">
                      <SelectTrigger className="h-auto w-[160px] rounded-lg border-0 bg-[#f6f6f6] px-3 py-3 text-sm font-normal text-[#3d3d3d] shadow-none focus:ring-0 dark:bg-white/10 dark:text-foreground [&_svg]:opacity-70">
                        <SelectValue placeholder={t('AUTO') || 'Auto'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">{t('AUTO') || 'Auto'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                        {t('WHEN_LIMITS_ARE_REACHED') || 'When limits are reached'}
                      </p>
                      <p className="mt-1 text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
                        {t('CHOOSE_WHAT_HAPPENS_AFTER_USAGE_LIMIT') ||
                          'Choose what happens after running into a usage limit.'}
                      </p>
                    </div>
                    <Select defaultValue="switch">
                      <SelectTrigger className="h-auto w-[160px] rounded-lg border-0 bg-[#f6f6f6] px-3 py-3 text-sm font-normal text-[#3d3d3d] shadow-none focus:ring-0 dark:bg-white/10 dark:text-foreground [&_svg]:opacity-70">
                        <SelectValue placeholder={t('SWITCH_MODEL') || 'Switch model'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="switch">{t('SWITCH_MODEL') || 'Switch model'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div> */}

                  {/* <div className="flex flex-col gap-4 border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <div>
                    <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                      {t('KNOWLEDGE') || 'Knowledge'}
                    </p>
                    <p className="mt-1 text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
                      {t('ATTACH_FILES_AS_KNOWLEDGE') ||
                        'Directly attach files to the project and all its chats as knowledge.'}
                    </p>
                  </div>
                  <div className="flex h-[171px] items-center justify-center rounded-lg border border-dashed border-[#e7e7e7] bg-[#f6f6f6] dark:border-border dark:bg-muted/30">
                    <p className="text-sm text-[#888] dark:text-muted-foreground">
                      {t('DRAG_AND_DROP_FILES_HERE') || 'Drag and drop files here'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      className="inline-flex h-[38px] items-center gap-1.5 rounded-[62px] border border-[#e7e7e7] bg-[#f6f6f6] px-6 text-base font-medium leading-[1.2] text-[#3d3d3d] dark:border-border dark:bg-white/10 dark:text-foreground"
                    >
                      <Plus width={24} height={24} strokeWidth={1.75} />
                      {t('ATTACH') || 'Attach'}
                    </button>
                    <p className="text-xs font-medium leading-[1.6] text-[#888] dark:text-muted-foreground">
                      {t('OR_DRAG_AND_DROP_FILES') || 'or drag and drop files here'}
                    </p>
                  </div>
                </div> */}

                  {/* <div className="flex flex-col gap-4 border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                        {t('ACTIONS') || 'Actions'}
                      </p>
                      <p className="mt-1 text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
                        {t('ADD_ACTIONS_FOR_AGENT') ||
                          'Add actions that your agent can perform. External tools, web search, folders, and more.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-[62px] border border-[#e7e7e7] bg-[#f6f6f6] px-6 text-base font-medium leading-[1.2] text-[#3d3d3d] dark:border-border dark:bg-white/10 dark:text-foreground"
                    >
                      <Plus width={24} height={24} strokeWidth={1.75} />
                      {t('ADD_ACTION') || 'Add Action'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-[#f6f6f6] px-4 py-3 dark:bg-white/10">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded border border-[#e7e7e7] bg-white dark:border-border dark:bg-card">
                        <Globe width={16} height={16} strokeWidth={1.75} className="text-[#3d3d3d]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                          {t('WEB_SEARCH') || 'Web search'}
                        </p>
                        <p className="text-sm leading-[1.2] text-[#3d3d3d] dark:text-muted-foreground">
                          {t('WEB_SEARCH_ACTION_DESCRIPTION') ||
                            'Searches the web to improve response quality, especially for factual or news'}
                        </p>
                      </div>
                    </div>
                    <MoreHoriz width={24} height={24} strokeWidth={1.75} className="shrink-0 text-[#3d3d3d]" />
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-[#f6f6f6] px-4 py-3 dark:bg-white/10">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded border border-[#e7e7e7] bg-white dark:border-border dark:bg-card">
                        <MediaImage width={16} height={16} strokeWidth={1.75} className="text-[#3d3d3d]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                          {t('IMAGE_GENERATION') || 'Image Generation'}
                        </p>
                        <p className="text-sm leading-[1.2] text-[#3d3d3d] dark:text-muted-foreground">
                          {t('IMAGE_GENERATION_ACTION_DESCRIPTION') ||
                            'Generates images in the chat using natural language'}
                        </p>
                      </div>
                    </div>
                    <MoreHoriz width={24} height={24} strokeWidth={1.75} className="shrink-0 text-[#3d3d3d]" />
                  </div>
                </div> */}
                </fieldset>
              </form>
            </Form>
          )}
          </div>
        </div>
      </div>

      <aside className="relative w-full shrink-0 lg:w-[340px] xl:w-[360px]">
        <div className="flex h-[min(70dvh,560px)] min-h-0 w-full flex-col lg:absolute lg:inset-0 lg:h-auto">
          <AgentsConfigPreviewSection
            agent={agent}
            projectKey={projectKey}
            isSaving={isSaving}
            lastSaveTime={lastSaveTime}
          />
        </div>
      </aside>
    </div>
  );
}
